import { paths } from "src/routes/paths"

import packageJson from "../package.json"

// ----------------------------------------------------------------------

type SiteConfig = {
    name: string
    icon: string
    serverUrl: string
    assetURL: string
    basePath: string
    version: string
    sentryUrl: string
    env: any
    mapsKey: string
    apiKey?: string
    apiSecret?: string
    sampleBulkJobFileUrl: string
    microsoftClientId: string
    MIXPANEL_KEY: string
    MIXPANEL_PROXY_URL: string
    googleClientId: string
}

export type ConfigValue = {
    site: SiteConfig
    auth: {
        method: "jwt" | "amplify" | "firebase" | "supabase" | "auth0"
        skip: boolean
        redirectPath: string
        redirectPathAiProcessing: string
    }
    mapbox: {
        apiKey: string
    }
    firebase: {
        appId: string
        apiKey: string
        projectId: string
        authDomain: string
        storageBucket: string
        measurementId: string
        messagingSenderId: string
    }
    amplify: { userPoolId: string; userPoolWebClientId: string; region: string }
    auth0: { clientId: string; domain: string; callbackUrl: string }
    supabase: { url: string; key: string }
}

// ----------------------------------------------------------------------
const env = (import.meta.env.MODE as "development" | "production" | "staging" | "local") || "development"
console.log(import.meta.env)

const siteConfigProd: SiteConfig = {
    name: `${window.location.origin?.includes("apna.co") ? "Apna" : "GetHired"} | ATS`,
    icon: window.location.origin?.includes("apna.co") ? "/favicon-apna.ico" : "/favicon-gh.ico",
    serverUrl: "https://api.production.get-hired.ai",
    assetURL: import.meta.env.VITE_ASSET_URL ?? "",
    basePath: import.meta.env.VITE_BASE_PATH ?? "",
    env,
    version: packageJson.version,
    sentryUrl: "https://7b961aca06d941e6485664cf03d845d6@o283849.ingest.us.sentry.io/4507899982970880",
    mapsKey: "AIzaSyDNbrCTSlFSOw_Te5CzmcBAL5JmmqaHmrg",
    sampleBulkJobFileUrl: "https://storage.googleapis.com/bluemachines-prod/samples/sample_leads_upload-gethired.csv",
    microsoftClientId: "f4304260-b62b-4c81-bd30-9e925f93b7ea",
    MIXPANEL_KEY: "b96e1914cc08adb5d3332330e947ca74",
    MIXPANEL_PROXY_URL: "https://api-mixpanel.infra.apna.co",
    googleClientId: "663356906464-snc04ak051nplft49etnlppv8do97apd.apps.googleusercontent.com",
}

const siteConfigStaging: SiteConfig = {
    name: `${window.location.origin?.includes("apna.co") ? "Apna" : "GetHired"} | ATS`,
    icon: window.location.origin?.includes("apna.co") ? "/favicon-apna.ico" : "/favicon-gh.ico",
    serverUrl: "https://api.staging.get-hired.ai",
    env,
    assetURL: import.meta.env.VITE_ASSET_URL ?? "",
    basePath: import.meta.env.VITE_BASE_PATH ?? "",
    version: packageJson.version,
    sentryUrl: "https://7b961aca06d941e6485664cf03d845d6@o283849.ingest.us.sentry.io/4507899982970880",
    mapsKey: "AIzaSyDNbrCTSlFSOw_Te5CzmcBAL5JmmqaHmrg",
    sampleBulkJobFileUrl: "https://storage.googleapis.com/bluemachines-staging/samples/sample_leads_upload.csv",
    microsoftClientId: "f4304260-b62b-4c81-bd30-9e925f93b7ea",
    MIXPANEL_KEY: "6be41536d5701ebe5974cad71e5d0ec4",
    MIXPANEL_PROXY_URL: "https://api-mixpanel-staging.infra.apna.co",
    googleClientId: "448902111299-hbpdnmsj3lne1rh31kj76l32fqi2iq5i.apps.googleusercontent.com",
}

export const CONFIG: ConfigValue = {
    site: env === "production" ? siteConfigProd : siteConfigStaging,
    /**
     * Auth
     * @method jwt | amplify | firebase | supabase | auth0
     */
    auth: {
        method: "jwt",
        skip: false,
        redirectPath: paths.dashboard.root,
        redirectPathAiProcessing: paths.dashboard.mandates,
    },
    /**
     * APMapboxI
     */
    mapbox: {
        apiKey: import.meta.env.VITE_MAPBOX_API_KEY ?? "",
    },
    /**
     * Firebase
     */
    firebase: {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "",
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "",
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
        appId: import.meta.env.VITE_FIREBASE_APPID ?? "",
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? "",
    },
    /**
     * Amplify
     */
    amplify: {
        userPoolId: import.meta.env.VITE_AWS_AMPLIFY_USER_POOL_ID ?? "",
        userPoolWebClientId: import.meta.env.VITE_AWS_AMPLIFY_USER_POOL_WEB_CLIENT_ID ?? "",
        region: import.meta.env.VITE_AWS_AMPLIFY_REGION ?? "",
    },
    /**
     * Auth0
     */
    auth0: {
        clientId: import.meta.env.VITE_AUTH0_CLIENT_ID ?? "",
        domain: import.meta.env.VITE_AUTH0_DOMAIN ?? "",
        callbackUrl: import.meta.env.VITE_AUTH0_CALLBACK_URL ?? "",
    },
    /**
     * Supabase
     */
    supabase: {
        url: import.meta.env.VITE_SUPABASE_URL ?? "",
        key: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "",
    },
}
